// TRAINING in-run HUD (owner 2026-11-02): the ARMORY prompt bubble over the pedestal the hero stands at, the DPS meter
// corner panel, per-dummy damage labels, tappable HUD skill icons (tap = fire now, long-press = its controls) with
// cooldown timers, and the on-screen TRAINING button. DOM only; reads the Sim and acts through sim.training.
import type { Sim } from '../core/sim';
import { MAX_PASSIVE_LEVEL, MAX_WEAPON_LEVEL, PASSIVES, WEAPONS, type PassiveId, type WeaponId } from '../core/content';
import { FUSIONS, type FusionId } from '../core/fusions';
import { BENCH_SECS } from '../core/training';
import { fusionIcon, techIcon, traitIcon } from './icons';

export interface TrainingHudHost {
  sim(): Sim;
  project(x: number, y: number, z: number): { x: number; y: number; vis: boolean };
  groundAt(x: number, z: number): number;
  openPanel(item?: string): void;
  click(): void; deny(): void;
  flush(from: number): void;
  /** 'touch' | 'gamepad' | 'keyboard' */
  device(): string;
  /** controller glyph for a face button */
  glyph(k: 'south' | 'north' | 'west' | 'east'): string;
  name(src: string): string;
  flag(f: 'meter' | 'tags'): boolean;
}

const $ = <T extends HTMLElement = HTMLElement>(s: string) => document.querySelector(s) as T;
const fmt = (n: number) => (n >= 10000 ? `${(n / 1000).toFixed(1)}k` : n >= 100 ? Math.round(n).toString() : n.toFixed(1));
const LONG_MS = 450;

export class TrainingHud {
  private promptKey = ''; private meterT = 0; private tagT = 0; private cdT = 0;
  private tags: HTMLElement[] = [];
  private press: { id: string; t: number; el: HTMLElement; done: boolean } | null = null;

  constructor(private readonly h: TrainingHudHost) {
    $('#tr-prompt').addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest('[data-a]') as HTMLElement | null; if (!b) return;
      this.act(b.dataset.a as 'toggle' | 'up' | 'down' | 'evolve' | 'max');
    });
    // HUD skill icons (training runs only: body.training gives them pointer events)
    const lo = $('#loadout');
    lo.addEventListener('pointerdown', (e) => {
      if (!this.h.sim().training) return;
      const el = (e.target as HTMLElement).closest('.slot[data-w], .slot[data-f]') as HTMLElement | null; if (!el) return;
      e.preventDefault();
      const id = el.dataset.w ?? el.dataset.f!;
      const p = { id, t: performance.now(), el, done: false }; this.press = p;
      setTimeout(() => { if (this.press === p && !p.done) { p.done = true; this.h.click(); this.h.openPanel(id); } }, LONG_MS);
    });
    const up = () => {
      const p = this.press; this.press = null; if (!p || p.done) return;
      p.done = true;
      const s = this.h.sim(), n0 = s.events.length;
      if (s.training?.triggerNow(p.id)) { this.h.click(); p.el.classList.remove('tap'); void p.el.offsetWidth; p.el.classList.add('tap'); this.h.flush(n0); }
      else this.h.deny();
    };
    lo.addEventListener('pointerup', up);
    lo.addEventListener('pointercancel', () => { this.press = null; });
    lo.addEventListener('pointerleave', () => { if (this.press && !this.press.done) this.press = null; });
  }

  /** armory actions on the pedestal the hero stands at; true = handled */
  act(a: 'toggle' | 'up' | 'down' | 'evolve' | 'max'): boolean {
    const s = this.h.sim(), t = s.training; if (!t || t.near < 0) return false;
    const n0 = s.events.length;
    if (t.armory(a)) { this.h.click(); this.h.flush(n0); this.promptKey = ''; } else this.h.deny();
    return true;
  }

  /** per frame (any in-run screen of a training run) */
  update(dt: number, playing: boolean): void {
    const s = this.h.sim(), t = s.training;
    document.body.classList.toggle('training', !!t);
    if (!t) { $('#tr-prompt').hidden = true; $('#tr-meter').hidden = true; this.hideTags(0); return; }
    this.prompt(s, playing);
    if ((this.meterT -= dt) <= 0) { this.meterT = 0.25; this.meter(s, playing); }
    if ((this.cdT -= dt) <= 0) { this.cdT = 0.1; this.cooldowns(s); }
    this.dummyTags(s, (this.tagT -= dt) <= 0); if (this.tagT <= 0) this.tagT = 0.125;
  }

  private prompt(s: Sim, playing: boolean): void {
    const t = s.training!, el = $('#tr-prompt'), ped = t.pedestals[t.near];
    if (!ped || !playing) { if (!el.hidden) { el.hidden = true; this.promptKey = ''; } return; }
    const pos = this.h.project(ped.x, this.h.groundAt(ped.x, ped.z) + 2.8, ped.z);
    el.style.transform = `translate(${Math.round(pos.x)}px, ${Math.round(pos.y)}px) translate(-50%, -100%)`;
    const dev = this.h.device();
    let lv = 0, max = 1, evo = false, name = '', icon = '', owned = false;
    if (ped.kind === 'skill') { const w = ped.id as WeaponId; lv = s.wlv(w); max = MAX_WEAPON_LEVEL; evo = s.evolved.has(w); owned = lv > 0; name = evo ? WEAPONS[w].evolvedName : WEAPONS[w].name; icon = techIcon(w, evo); }
    else if (ped.kind === 'trait') { const p = ped.id as PassiveId; lv = s.plv(p); max = PASSIVES[p].max ?? MAX_PASSIVE_LEVEL; owned = lv > 0; name = PASSIVES[p].name; icon = traitIcon(p); }
    else { const f = ped.id as FusionId; owned = s.fusion.has(f); name = FUSIONS[f].name; icon = fusionIcon(f); }
    const key = `${t.near}|${lv}|${evo}|${owned}|${dev}|${s.techSlotsUsed}`;
    if (key === this.promptKey && !el.hidden) return;
    this.promptKey = key; el.hidden = false;
    const g = (k: 'south' | 'north') => this.h.glyph(k);
    const k = (kb: string, pad: string) => (dev === 'gamepad' ? `<kbd>${pad}</kbd>` : dev === 'keyboard' ? `<kbd>${kb}</kbd>` : '');
    let btns = '';
    if (ped.kind === 'fusion') {
      const f = FUSIONS[ped.id as FusionId];
      btns = `<button class="tc ${owned ? 'drop' : 'fuse'}" data-a="toggle">${k('E', g('south'))}${owned ? 'UNFUSE' : 'FUSE NOW'}</button>`;
      el.innerHTML = `<div class="tp-h">${icon}<span><b>${name}</b><small>${WEAPONS[f.a].name} + ${WEAPONS[f.b].name}${owned ? ' · owned' : ''}</small></span></div><div class="tp-b">${btns}</div>`;
      return;
    }
    btns = `<button class="tc ${owned ? 'drop' : 'add'}" data-a="toggle">${k('E', g('south'))}${owned ? 'DROP' : 'EQUIP'}</button>`
      + `<button class="tc" data-a="down"${lv <= 0 ? ' disabled' : ''}>${k('Q', '◀')}RANK −</button><button class="tc" data-a="up"${lv >= max ? ' disabled' : ''}>${k('R', '▶')}RANK +</button>`
      + (ped.kind === 'skill' ? `<button class="tc${evo ? ' on' : ''}" data-a="evolve">${k('X', g('north'))}EVOLVE</button>` : '')
      + `<button class="tc" data-a="max">MAX</button>`;
    const sub = owned ? `rank ${lv} / ${max}${evo ? ' · evolved' : ''}` : ped.kind === 'skill' && s.techSlotsUsed >= 6 && !s.training!.noSlotLimit ? 'slots full — drop one first' : 'not equipped';
    el.innerHTML = `<div class="tp-h">${icon}<span><b>${name}</b><small>${sub}</small></span></div><div class="tp-b">${btns}</div>`;
  }

  private meter(s: Sim, playing: boolean): void {
    const t = s.training!, el = $('#tr-meter');
    const on = playing && (this.h.flag('meter') || !!t.bench); // (hidden under the panel / pause menu)
    el.hidden = !on; if (!on) return;
    const m = t.meter().slice(0, 8), tot = t.meter().reduce((a, q) => a + q.dps, 0);
    el.innerHTML = `<div class="tm-h"><b>DPS</b><span>${t.bench ? `TEST ${Math.ceil(t.benchLeft)} s` : t.benchResult ? `last test ${fmt(t.benchResult.dps)}` : 'last 10 s'}</span></div>`
      + (m.length ? m.map((q) => `<div class="tm-r"><span>${this.h.name(q.src)}</span><b>${fmt(q.dps)}</b></div>`).join('') + `<div class="tm-r tot"><span>TOTAL</span><b>${fmt(tot)}</b></div>` : '<div class="tm-r"><span>no damage yet</span></div>')
      + (t.bench ? `<div class="tm-bar"><i style="width:${(100 * (1 - t.benchLeft / BENCH_SECS)).toFixed(1)}%"></i></div>` : '');
  }

  private cooldowns(s: Sim): void {
    const t = s.training!;
    document.querySelectorAll<HTMLElement>('#loadout .slot[data-w], #loadout .slot[data-f]').forEach((el) => {
      let cd = 0;
      if (el.dataset.w) { const w = el.dataset.w as WeaponId; cd = w === 'lance' ? s.player.beamCd : w === 'orbit' ? 0 : s.wTimers[w] ?? 0; if (cd > 60) cd = 0; }
      else { const o = s.fusion.owned.get(el.dataset.f as FusionId); cd = o ? Math.max(0, o.t) : 0; }
      let em = el.querySelector('em.cdt') as HTMLElement | null;
      if (!em) { em = document.createElement('em'); em.className = 'cdt'; el.appendChild(em); }
      const txt = t.manualFire ? 'TAP' : cd > 0.05 ? cd.toFixed(cd < 10 ? 1 : 0) : '';
      if (em.textContent !== txt) em.textContent = txt;
    });
  }

  private hideTags(from: number): void { for (let i = from; i < this.tags.length; i++) if (!this.tags[i].hidden) this.tags[i].hidden = true; }
  private dummyTags(s: Sim, text: boolean): void {
    const t = s.training!;
    if (!this.h.flag('tags')) { this.hideTags(0); return; }
    const box = $('#tr-tags'); let n = 0;
    for (const e of s.enemies) {
      if (!e.alive || !e.dummy || n >= 40) continue;
      const tag = this.tags[n] ?? (this.tags[n] = Object.assign(document.createElement('div'), { className: 'tr-tag' }));
      if (!tag.parentElement) box.appendChild(tag);
      const top = e.def.radius / 0.75 * 2.4 * (e.elite ? 1.25 : 1);
      const pos = this.h.project(e.x, this.h.groundAt(e.x, e.z) + top, e.z);
      tag.hidden = !pos.vis;
      tag.style.transform = `translate(${Math.round(pos.x)}px, ${Math.round(pos.y)}px) translate(-50%, -100%)`;
      if (text) {
        const r = t.dummyDps(e.id), fi = s.bossFormInfo(e);
        tag.innerHTML = `<b>${fmt(r.dps)}</b> DPS${fi ? `<small>FORM ${fi.form + 1}/3 · ${fi.transforming > 0 ? 'IMMUNE' : `${Math.round(e.hp / e.maxHp * 100)}%${fi.form < 2 ? ` → gate ${[67, 33][fi.form]}%` : ''}`}</small>` : ''}`;
      }
      n++;
    }
    this.hideTags(n);
  }
}
